import React from 'react';
import {useReports} from '@sail/observability';
import type {ProjectName} from '@sail/observability';
import type {PrimitiveRecord} from './ErrorTypes';

export const isInDev = (): boolean => process.env.NODE_ENV !== 'production';

export const warnInDev = (...args: Array<unknown>): void => {
  // eslint-disable-next-line no-console
  if (isInDev() && console && console.warn) {
    // console is stubbed away in some tests :-(
    // eslint-disable-next-line no-console
    console.warn(...args);
  }
};

const useAssert = () => {
  const {error, warning} = useReports();

  return React.useCallback(
    (
      level: 'error' | 'warning',
      condition: boolean,
      message: string,
      owner: ProjectName,
      extras?: PrimitiveRecord | undefined,
    ) => {
      if (condition) {
        return;
      }

      const err = new Error(message);
      const tags = {level, owner};

      const reportFn = level === 'error' ? error : warning;

      reportFn(err, {tags, extras});

      if (
        process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test'
      ) {
        // eslint-disable-next-line no-console
        const logFn = level === 'error' ? console.error : console.warn;

        logFn(err, {tags, extras});
      }
    },
    [error, warning],
  );
};

/**
 * A hook that asserts a condition.
 * In development, this logs the provided `message` to the console using `console.error`.
 * In production, this does not log to the console, but instead sends the error to Sentry.
 * No `Error` object is thrown in any environment.
 *
 * @returns A `firmAssert` function
 */
export const useFirmAssert = () => {
  const assertFn = useAssert();

  return React.useCallback(
    (
      condition: boolean,
      message: string,
      owner: ProjectName,
      extra?: PrimitiveRecord | undefined,
    ) => {
      assertFn('error', condition, message, owner, extra);
    },
    [assertFn],
  );
};

/**
 * A hook that asserts a condition.
 * In development, this logs the provided `message` to the console using `console.warn`.
 * In production, this does not log to the console, but instead sends the error to Sentry.
 * No `Error` object is thrown in any environment.
 *
 * @returns A `softAssert` function
 */
export const useSoftAssert = () => {
  const assertFn = useAssert();

  return React.useCallback(
    (
      condition: boolean,
      message: string,
      owner: ProjectName,
      extra?: PrimitiveRecord | undefined,
    ) => {
      assertFn('warning', condition, message, owner, extra);
    },
    [assertFn],
  );
};

/**
 * Identical to the `useFirmAssert` hook, but with the `condition` parameter hardcoded to `false`.
 *
 * @returns A `firmAssertFailed` function
 */
export const useFirmAssertFailed = () => {
  const assertFn = useAssert();

  return React.useCallback(
    (
      message: string,
      owner: ProjectName,
      extra?: PrimitiveRecord | undefined,
    ) => {
      assertFn('error', false, message, owner, extra);
    },
    [assertFn],
  );
};

/**
 * Identical to the `useSoftAssert` hook, but with the `condition` parameter hardcoded to `false`.
 *
 * @returns A `softAssertFailed` function
 */
export const useSoftAssertFailed = () => {
  const assertFn = useAssert();

  return React.useCallback(
    (
      message: string,
      owner: ProjectName,
      extra?: PrimitiveRecord | undefined,
    ) => {
      assertFn('warning', false, message, owner, extra);
    },
    [assertFn],
  );
};
